/**
 * Artefact routes: first URL segment is `route_number` on each persona in personas.json
 * (exposed via GET /api/personas). Legacy bookmarks may still use initials, hyphenated keys,
 * or aliases — those resolve via resolvePersonaKeyFromArtefactSegment.
 */

/** Read numeric route segment string from persona payload, or null if unset/invalid. */
export function artefactRouteSegmentFromPersona(personaKey, personas) {
  const p = personaKey && personas && typeof personas === 'object' ? personas[personaKey] : null;
  if (!p || typeof p !== 'object') return null;
  const n = p.route_number;
  if (n == null || n === '') return null;
  const num = typeof n === 'number' ? n : parseInt(String(n).trim(), 10);
  if (!Number.isFinite(num) || num < 1) return null;
  return String(num);
}

/** Collapses whitespace in persona keys to single hyphens for URL segments (fallback only). */
export function slugifyArtefactSegment(key) {
  let s = String(key ?? '').trim();
  if (!s) return '';
  // Keys accidentally passed as partial URI encoding (e.g. "The%20Artificial...")
  try {
    if (/%[0-9A-Fa-f]{2}/.test(s)) {
      s = decodeURIComponent(s.replace(/\+/g, ' '));
    }
  } catch {
    /* keep s */
  }
  return s
    .normalize('NFKC')
    .replace(/\s+/gu, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function encodeArtefactPathSegment(personaKey) {
  const slug = slugifyArtefactSegment(personaKey);
  return encodeURIComponent(slug).replace(/%20/g, '-');
}

/**
 * Map legacy route/session strings to canonical persona keys using `persona_key_aliases`
 * from personas.json (via GET /api/personas).
 */
function slugMatchesSegment(slug, seg) {
  if (!slug || !seg) return false;
  return slug.toLowerCase() === String(seg).trim().toLowerCase();
}

function resolveBackendAliases(seg, personaKeyAliases, personas) {
  if (!seg || !personaKeyAliases || typeof personaKeyAliases !== 'object') return seg;
  if (!personas || typeof personas !== 'object' || Object.keys(personas).length === 0) {
    return seg;
  }

  const slugSeg = slugifyArtefactSegment(seg);
  const slugSegLower = slugSeg.toLowerCase();
  const direct = personaKeyAliases[seg] ?? personaKeyAliases[slugSeg];
  if (typeof direct === 'string' && personas[direct]) return direct;

  for (const [from, to] of Object.entries(personaKeyAliases)) {
    if (typeof from !== 'string' || typeof to !== 'string') continue;
    if (!personas[to]) continue;
    if (slugMatchesSegment(slugifyArtefactSegment(from), slugSegLower)) return to;
    if (from.toLowerCase() === slugSegLower) return to;
  }
  return seg;
}

/** Map decoded path segment back to backend persona key using loaded personas. */
export function resolvePersonaKeyFromArtefactSegment(decodedSegment, personas, personaKeyAliases) {
  if (!decodedSegment || !personas || typeof personas !== 'object') return decodedSegment;
  let seg = resolveBackendAliases(String(decodedSegment).trim(), personaKeyAliases, personas);
  const keys = Object.keys(personas);
  if (keys.includes(seg)) return seg;

  if (/^\d+$/.test(seg)) {
    const n = parseInt(seg, 10);
    for (const k of keys) {
      const rn = personas[k]?.route_number;
      const num = typeof rn === 'number' ? rn : rn != null && rn !== '' ? parseInt(String(rn), 10) : NaN;
      if (Number.isFinite(num) && num === n) return k;
    }
  }

  const segUpper = seg.toUpperCase();
  const segLower = seg.toLowerCase();
  for (const k of keys) {
    const ini = personas[k]?.initials;
    if (typeof ini === 'string' && ini.trim().toUpperCase() === segUpper) return k;
  }
  for (const k of keys) {
    if (slugMatchesSegment(slugifyArtefactSegment(k), segLower)) return k;
  }
  for (const k of keys) {
    const orig = personas[k]?.original_name;
    if (typeof orig !== 'string') continue;
    if (orig === seg) return k;
    if (slugMatchesSegment(slugifyArtefactSegment(orig), segLower)) return k;
  }
  return seg;
}

/**
 * Normalize snapshots, router state, and bookmarks to current persona keys in personas.json.
 * `personaKeyAliases` comes from the API (`persona_key_aliases` in personas.json).
 */
export function canonicalPersonaKey(rawKey, personas, personaKeyAliases) {
  if (rawKey == null || rawKey === '') return rawKey;
  let seg = String(rawKey).trim();
  try {
    if (/%[0-9A-Fa-f]{2}/.test(seg)) {
      seg = decodeURIComponent(seg.replace(/\+/g, ' '));
    }
  } catch {
    /* keep seg */
  }

  if (personas && typeof personas === 'object' && Object.keys(personas).length > 0) {
    return resolvePersonaKeyFromArtefactSegment(seg, personas, personaKeyAliases);
  }

  return seg;
}

/** @returns {{ key: string, view: 'introduction'|'preferences'|'chat'|'disclaimer'|'aura' } | null} */
export function parseArtefactSessionPath(pathname) {
  if (typeof pathname !== 'string' || !pathname.startsWith('/')) return null;
  const parts = pathname.split('/').filter(Boolean);

  if (parts.length !== 2) return null;
  const [keySeg, view] = parts;
  if (
    view !== 'introduction' &&
    view !== 'preferences' &&
    view !== 'disclaimer' &&
    view !== 'chat' &&
    view !== 'aura'
  ) {
    return null;
  }
  try {
    return { key: decodeURIComponent(keySeg), view };
  } catch {
    return { key: keySeg, view };
  }
}

/** First-path segments that are app routes, not artefact slugs (must stay aligned with App.js Routes). */
export const RESERVED_ARTEFACT_ROUTE_SEGMENTS = new Set(['analytics', 'overview', 'chat']);

/**
 * @returns {string | null} decoded segment for bare paths like `/1`, not `/1/preferences`
 */
export function parseBareArtefactSegment(pathname) {
  if (typeof pathname !== 'string' || !pathname.startsWith('/')) return null;
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length !== 1) return null;
  try {
    return decodeURIComponent(parts[0]);
  } catch {
    return parts[0];
  }
}

/** True when pathname is a single segment that can be an artefact landing (`/:artefactsKey`). */
export function isBareArtefactLandingPath(pathname) {
  const seg = parseBareArtefactSegment(pathname);
  if (!seg) return false;
  return !RESERVED_ARTEFACT_ROUTE_SEGMENTS.has(seg.toLowerCase());
}

/**
 * @param {string} personaKey — canonical key from personas.json (e.g. "Artificial Womb")
 * @param {'introduction'|'preferences'|'chat'|'disclaimer'|'aura'} view
 * @param {Record<string, { route_number?: number }> | null | undefined} personas — `route_number` sets the first path segment
 */
export function artefactPath(personaKey, view, personas = null) {
  const seg = artefactRouteSegmentFromPersona(personaKey, personas);
  const baseSeg = seg || encodeArtefactPathSegment(personaKey);
  return `/${baseSeg}/${view}`;
}

const _SESSION_VIEWS = new Set(['introduction', 'preferences', 'chat', 'disclaimer', 'aura']);

/**
 * Canonical pathname for the current route when the segment resolves to a persona
 * (e.g. /artificial-womb/chat → /1/chat). Returns null when not an artefact route.
 */
export function canonicalArtefactPathFromPathname(pathname, personas, personaKeyAliases) {
  if (!pathname || !personas || typeof personas !== 'object') return null;

  const parsed = parseArtefactSessionPath(pathname);
  if (parsed) {
    const resolved = canonicalPersonaKey(parsed.key, personas, personaKeyAliases);
    if (!personas[resolved]) return null;
    const view = _SESSION_VIEWS.has(parsed.view) ? parsed.view : 'chat';
    return artefactPath(resolved, view, personas);
  }

  const bare = parseBareArtefactSegment(pathname);
  if (!bare || RESERVED_ARTEFACT_ROUTE_SEGMENTS.has(bare.toLowerCase())) return null;
  const resolved = canonicalPersonaKey(bare, personas, personaKeyAliases);
  if (!personas[resolved]) return null;
  const seg = artefactRouteSegmentFromPersona(resolved, personas);
  const baseSeg = seg || encodeArtefactPathSegment(resolved);
  return `/${baseSeg}`;
}
